// =============================================================================
// DOELSTELLINGEN TYPES
// Re-exported from original types.ts for consistency
// =============================================================================

export type ConfidenceLevel = 'low' | 'medium' | 'high';

export interface ActionItem {
  id: string;
  title: string;
  status: 'planned' | 'in-progress' | 'done' | 'at-risk';
  plannedMinutes?: number;
  actualMinutes?: number;
  scheduledFor?: Date | null;
  completedAt?: Date | null;
  allowManualComplete?: boolean;
}

export interface ActivityItem {
  id: string;
  title: string;
  status: 'planned' | 'in-progress' | 'completed';
  actions: ActionItem[];
  plannedMinutes?: number;
  actualMinutes?: number;
  locked?: boolean;
  progress?: number;
  completed?: boolean;
}

export interface MilestoneItem {
  id: string;
  title: string;
  targetDate?: Date | null;
  achieved?: boolean;
  criteria?: string[];
  activities: string[];
  confirmationRequired?: boolean;
}

export interface MainGoal {
  id: string;
  title: string;
  horizon?: string;
}

export interface CapabilityAwareness {
  knowledge?: string[];
  skills?: string[];
  training?: string[];
  roles?: string[];
  tools?: string[];
  financesNote?: string;
}

export interface TimeSummary {
  plannedMinutes: number;
  actualMinutes: number;
  varianceMinutes: number;
  status: 'ahead' | 'on-track' | 'at-risk' | 'behind';
}

export interface TimelineMilestoneMarker {
  id: string;
  label: string;
  position: number; // 0-1
  completed?: boolean;
}

export interface TimeEstimate {
  label: string;
  minMinutes: number;
  maxMinutes: number;
  confidence: ConfidenceLevel;
}

export interface TimeEstimateResult extends TimeEstimate {
  rationale: string;
}

// =============================================================================
// FIREBASE TYPES (for Firestore persistence)
// These are the canonical types used for Firebase CRUD operations
// =============================================================================

// Goal Type: A = eerder behaald, B = bekend via anderen, C = groot/onbekend
export type GoalType = 'A' | 'B' | 'C';

// Goal Main Category: used in intake step 0 to determine goal context
export type GoalMainCategory = 'personal' | 'business' | 'team';

// Goal Status: draft → committed (blokkering voor sparring/milestones tot committed)
export type GoalStatus = 'draft' | 'committed';

// Commitment signature data
export interface GoalCommitment {
  name: string;           // Ondertekende naam
  date: string;           // ISO date string van ondertekening
  confirmed: boolean;     // Checkbox bevestiging
  videoUrl?: string;      // Optionele startvideo URL
}

// Questionnaire answers for goal type determination
export interface GoalQuestionnaireAnswers {
  achievedBefore: 'yes' | 'no';           // Eerder behaald?
  seenInOthers: 'yes' | 'no';             // Bij anderen gezien?
  feelingAboutGoal: 'comfortable' | 'exciting'; // Comfortabel of spannend?
  knowsHow: 'yes' | 'partially' | 'no';   // Weet je hoe?
}

// Levensbalans categorieën voor doelen (voor tijdstracking)
export type GoalBalanceCategory = 'work' | 'self' | 'family' | 'activity';

export const GOAL_BALANCE_LABELS: Record<GoalBalanceCategory, string> = {
  work: 'Werk',
  self: 'Persoonlijk',
  family: 'Familie',
  activity: 'Activiteit'
};

export interface Goal {
  id: string;
  ownerId: string;        // Firebase Auth UID
  title: string;
  description?: string;
  startDate: string;      // ISO date string "2026-01-15"
  endDate: string;        // ISO date string "2026-06-30"
  createdAt: number;      // Date.now() timestamp
  
  // === LEVENSBALANS ===
  balanceCategory?: GoalBalanceCategory;       // Voor tijdstracking: werk, persoonlijk, familie, activiteit
  
  // === FASE 1 UITBREIDINGEN ===
  status: GoalStatus;                          // draft | committed
  goalType?: GoalType;                         // A | B | C (na questionnaire)
  questionnaireAnswers?: GoalQuestionnaireAnswers; // Antwoorden vragenlijst
  commitment?: GoalCommitment;                 // Ondertekening data
  
  // === MISSIE & VISIE ===
  mission?: string;                            // De impact/bijdrage - het "waarom"
  vision?: string;                             // Het ideaalbeeld - het "waarheen"
  
  // === DOCUMENTEN ===
  documents?: AttachedDocument[];              // Bijlagen bij dit doel
  
  // === GOAL CATEGORY (stap 0 intake) ===
  goalMainCategory?: GoalMainCategory;         // personal | business | team

  // === TEAM/PROJECT UITBREIDINGEN ===
  isTeamGoal?: boolean;                        // Distinguish team vs personal
  teamMembers?: TeamMember[];                  // Team roster
  projectDefinition?: ProjectDefinition;        // From project sparring
}

// Document attached to milestone or goal
export interface AttachedDocument {
  id: string;
  name: string;           // Original filename
  url: string;            // Firebase Storage URL
  size: number;           // File size in bytes
  type: string;           // MIME type
  uploadedAt: number;     // Timestamp
}

export interface Milestone {
  id: string;
  ownerId: string;        // Firebase Auth UID
  goalId: string;         // Foreign key → Goal.id
  title: string;
  targetDate: string;     // ISO date string
  createdAt: number;
  
  // === FASE 2 UITBREIDINGEN (Sparring) ===
  meaning?: string;              // "Wat betekent dit voor mij?" (antwoord gebruiker)
  sparringQuestion?: string;     // De vraag die leidde tot dit antwoord
  globalPeriod?: string;         // "Q1 2026", "maart 2026" (geen exacte datum)
  fromSparring?: boolean;        // true als uit sparring sessie ontstaan
  sparringSessionId?: string;    // Link naar SparringSession.id
  
  // === FASE 9 UITBREIDINGEN (Capability) ===
  origin?: 'manual' | 'sparring' | 'capability' | 'selftest';  // Bron van mijlpaal
  category?: MilestoneCategory;  // Categorie van deze mijlpaal
  
  // === DOCUMENTEN ===
  documents?: AttachedDocument[]; // Bijlagen bij deze mijlpaal
  
  // === TEAM/PROJECT UITBREIDINGEN ===
  assignedTo?: string;           // userId
  assignedToName?: string;       // Display name
  assignedToPhoto?: string;      // Avatar URL
}

export type ActionOrigin = 'manual' | 'intake' | 'sparring' | 'capability' | 'milestone_generated' | 'selftest';

// =============================================================================
// ACTIE PRIORITEIT & TYPE SYSTEEM
// =============================================================================

/**
 * Actie prioriteit - bepaalt urgentie en volgorde
 */
export type ActionPriority = 'high' | 'medium' | 'low';

/**
 * Actie type - gebaseerd op fase/categorie van de mijlpaal
 * Mapping: MilestoneCategory → ActionType
 */
export type ActionType = 
  | 'research'    // Onderzoek & leren (richting_keuzes, vaardigheden_kennis)
  | 'plan'        // Plannen & structureren (systemen_structuur)
  | 'build'       // Bouwen & uitvoeren (doorbraak_uitvoering)
  | 'validate'    // Testen & valideren (zichtbaarheid_markt)
  | 'grow';       // Schalen & groeien (opschaling_hefboom)

/**
 * Prioriteit configuratie met kleuren (Tailwind classes)
 */
export const ACTION_PRIORITY_CONFIG: Record<ActionPriority, {
  label: string;
  emoji: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  dotColor: string;
}> = {
  high: {
    label: 'Hoog',
    emoji: '●',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    dotColor: 'bg-red-500',
  },
  medium: {
    label: 'Gemiddeld',
    emoji: '●',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    dotColor: 'bg-amber-500',
  },
  low: {
    label: 'Laag',
    emoji: '●',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
    dotColor: 'bg-green-500',
  },
};

/**
 * Actie type configuratie met kleuren en iconen
 */
export const ACTION_TYPE_CONFIG: Record<ActionType, {
  label: string;
  emoji: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  research: {
    label: 'Onderzoek',
    emoji: '◉',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  plan: {
    label: 'Plannen',
    emoji: '◉',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
  build: {
    label: 'Bouwen',
    emoji: '◉',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
  },
  validate: {
    label: 'Valideren',
    emoji: '✓',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-700',
    borderColor: 'border-teal-200',
  },
  grow: {
    label: 'Groeien',
    emoji: '↗',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
  },
};

/**
 * Mapping van MilestoneCategory naar ActionType
 */
export const MILESTONE_CATEGORY_TO_ACTION_TYPE: Record<MilestoneCategory, ActionType> = {
  richting_keuzes: 'research',
  vaardigheden_kennis: 'research',
  systemen_structuur: 'plan',
  zichtbaarheid_markt: 'validate',
  opschaling_hefboom: 'grow',
  doorbraak_uitvoering: 'build',
};

/**
 * Bepaal ActionType op basis van actie-tekst als er geen categorie is
 */
export function detectActionTypeFromTitle(title: string): ActionType {
  const lowerTitle = title.toLowerCase();
  
  // Research indicators
  if (lowerTitle.includes('onderzoek') || lowerTitle.includes('leer') || 
      lowerTitle.includes('verzamel') || lowerTitle.includes('analyseer') ||
      lowerTitle.includes('bestudeer') || lowerTitle.includes('ontdek')) {
    return 'research';
  }
  
  // Plan indicators
  if (lowerTitle.includes('plan') || lowerTitle.includes('schets') || 
      lowerTitle.includes('bepaal') || lowerTitle.includes('definieer') ||
      lowerTitle.includes('organiseer') || lowerTitle.includes('structuur')) {
    return 'plan';
  }
  
  // Validate indicators
  if (lowerTitle.includes('test') || lowerTitle.includes('valideer') || 
      lowerTitle.includes('feedback') || lowerTitle.includes('deel') ||
      lowerTitle.includes('presenteer') || lowerTitle.includes('toon')) {
    return 'validate';
  }
  
  // Grow indicators
  if (lowerTitle.includes('schaal') || lowerTitle.includes('automatiseer') || 
      lowerTitle.includes('optimaliseer') || lowerTitle.includes('groei') ||
      lowerTitle.includes('uitbreid') || lowerTitle.includes('verbeter')) {
    return 'grow';
  }
  
  // Default to build (most common)
  return 'build';
}

export interface Action {
  id: string;
  ownerId: string;        // Firebase Auth UID
  goalId: string;         // Foreign key → Goal.id
  milestoneId?: string | null;  // Optional foreign key → Milestone.id
  title: string;
  completed: boolean;     // Alleen true/false, geen status strings
  createdAt: number;
  completedAt?: number;   // Timestamp wanneer voltooid
  origin?: ActionOrigin;
  plannedDate?: string;
  // Timer tracking (voor meetbaarheid)
  activeTime?: number;           // Totale actieve tijd in milliseconden
  timerStartedAt?: number | null; // Timestamp wanneer timer gestart (null = niet actief)
  isTimerRunning?: boolean;      // Timer actief?
  order?: number;                // Volgorde in lijst (voor drag & drop reordering)
  // === PRIORITEIT & TYPE (NIEUW) ===
  priority?: ActionPriority;     // hoog | gemiddeld | laag
  actionType?: ActionType;       // research | plan | build | validate | grow
  // === DOMEIN (NIEUW) ===
  domain?: string;               // CapabilityDomain waar deze actie bij hoort (strategy, marketing, sales, etc.)
  // === FOCUS (NIEUW) ===
  isFocus?: boolean;             // true = actie staat in Focus tab (voor korte termijn focus op lange termijn doelen)
  // === TEAM/PROJECT UITBREIDINGEN ===
  assignedTo?: string;           // userId
  assignedToName?: string;       // Display name
}

// =============================================================================
// FASE 2: SPARRING TYPES
// Sparring = denken, betekenis, herkenningspunten (GEEN planning, GEEN acties)
// =============================================================================

// Sparring mode: structured (coach-vragen), free (open gesprek), reflection (reflectievragen), of project (project definitie)
export type SparringMode = 'structured' | 'free' | 'reflection' | 'project';

// Individual message in a sparring session
export interface SparringMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  milestoneCandidate?: boolean;  // AI detecteerde potentiële mijlpaal
  editedAt?: number;             // Timestamp van laatste bewerking
}

// Sparring session linked to a committed goal
export interface SparringSession {
  id: string;
  ownerId: string;              // Firebase Auth UID
  goalId: string;               // Foreign key → Goal.id (must be committed)
  mode: SparringMode;           // structured | free
  messages: SparringMessage[];  // Conversation history
  createdAt: number;
  closedAt?: number;            // Timestamp when session was closed
  milestonesCreated: string[];  // IDs van gecreëerde milestones uit deze sessie
}

// =============================================================================
// DOELSTELLINGEN NODE TYPES (legacy - for backward compatibility)
// These will be phased out as we migrate to Firebase types
// =============================================================================

export interface GoalNode {
  id: string;
  title: string;
  description?: string;  // Uitgebreide beschrijving voor context en scope
  startDate?: string;
  endDate?: string;
}

export interface MilestoneNode {
  id: string;
  title: string;
  targetDate?: string;
  goalId: string;
}

export interface ActionNode {
  id: string;
  title: string;
  description?: string;
  milestoneId?: string;
  goalId?: string;
  completed?: boolean;
  estimatedMinutes?: number;
  actualMinutes?: number;
  scheduledFor?: Date | null;
}

// =============================================================================
// TIMELINE TYPES
// =============================================================================

export interface GoalTimeline {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
}

export interface MilestoneTimeline {
  id: string;
  goalId: string;
  date: Date;
}

// =============================================================================
// EXCLUSION TYPES (for vacation/sickness)
// =============================================================================

export type TimeExclusionType = 'vacation' | 'sickness';

export interface TimeExclusionNode {
  id: string;
  type: TimeExclusionType;
  startDate: Date;
  endDate: Date;
}

// =============================================================================
// EDIT STATE TYPES (for inline editing)
// =============================================================================

export type EditingField = {
  id: string;
  field: 'title' | 'startDate' | 'endDate' | 'targetDate' | 'description';
} | null;

// =============================================================================
// FASE 4: JOURNEY TYPES
// Journey = reflectie, herinnering, chronologisch overzicht (GEEN voortgang)
// =============================================================================

// Entry types in the journey
export type JourneyEntryType = 'start' | 'reflection' | 'milestone' | 'closing';

// Single entry in the journey timeline
export interface JourneyEntry {
  id: string;
  goalId: string;               // Foreign key → Goal.id
  ownerId: string;              // Firebase Auth UID
  type: JourneyEntryType;       // start | reflection | milestone | closing
  milestoneId?: string;         // Optional link to Milestone.id (when type = 'milestone')
  videoUrl?: string;            // Optional video URL
  textContent?: string;         // Optional text content
  createdAt: number;            // Timestamp
}

// =============================================================================
// FASE 6: JOURNEY SHARING TYPES
// Binary sharing: shared with community OR not shared
// Read-only, chronological, no filters, no status
// =============================================================================

// Journey share record - MINIMAL
export interface JourneyShare {
  id: string;
  goalId: string;
  ownerId: string;                // Owner who created the share
  sharedEntryIds: string[];       // Which entries are shared (empty = all)
  sharedAt: number;               // When sharing was enabled
  isActive: boolean;              // Shared with community or not
}

// Read-only view of shared journey (for community)
export interface SharedJourneyView {
  shareId: string;
  goalTitle: string;
  goalDescription?: string;
  entries: JourneyEntry[];        // Filtered to sharedEntryIds
  sharedAt: number;
}

// =============================================================================
// FASE 8: CONTINUITY TYPES
// Minimal, decoupled, no tracking, no timestamps except creation
// =============================================================================

// Takeaway - completely decoupled from Journey
// No goalId, no journeyId, no foreign keys
export interface Takeaway {
  id: string;
  ownerId: string;
  textContent: string;
  createdAt: number;
}

// =============================================================================
// FASE 7: JOURNEY REFLECTION TYPES
// Private, optional, no analysis, no interpretation
// =============================================================================

// Reflection record - ALWAYS PRIVATE
// Note: id is Firestore document ID, not in write payload
export interface JourneyReflection {
  id: string;                     // Firestore document ID (read-only)
  goalId: string;
  ownerId: string;
  entryId?: string | null;        // Optional: linked to specific entry
  textContent?: string | null;    // May be empty/null
  createdAt: number;              // Server timestamp
  isPrivate: true;                // Always true, not configurable
}

// =============================================================================
// FASE 9: CHANGELOG TYPES
// Tracks all changes to goals, milestones, and videos for reflection
// =============================================================================

// Types of changes that can be logged
export type ChangeLogAction = 
  // Goal actions
  | 'goal_created'
  | 'goal_title_changed'
  | 'goal_description_changed'
  | 'goal_start_date_changed'
  | 'goal_end_date_changed'
  | 'goal_completed'
  // Milestone actions
  | 'milestone_created'
  | 'milestone_title_changed'
  | 'milestone_date_changed'
  | 'milestone_completed'
  | 'milestone_deleted'
  // Video actions
  | 'video_recorded'
  | 'video_rerecorded'
  | 'video_deleted'
  // Journey actions
  | 'journey_started'
  | 'journey_completed';

// Single change log entry
export interface ChangeLogEntry {
  id: string;
  goalId: string;               // Foreign key → Goal.id
  ownerId: string;              // Firebase Auth UID
  action: ChangeLogAction;      // Type of change
  entityType: 'goal' | 'milestone' | 'video' | 'journey';
  entityId?: string;            // ID of the changed entity (milestone, video, etc.)
  entityTitle?: string;         // Title/name of entity for display
  previousValue?: string;       // Old value (for changes)
  newValue?: string;            // New value (for changes)
  metadata?: Record<string, any>; // Extra data (e.g., re-record count)
  createdAt: number;            // Timestamp
}

// =============================================================================
// FASE 10: VISIONBOARD TYPES
// Visual collage of inspiration images per goal
// =============================================================================

// Single image in the visionboard
export interface VisionboardImage {
  id: string;
  goalId: string;               // Foreign key → Goal.id
  ownerId: string;              // Firebase Auth UID
  imageUrl: string;             // Firebase Storage URL or external URL
  caption?: string;             // Optional text description
  order: number;                // Position in the grid (for drag & drop)
  sourceType: 'upload' | 'url'; // How the image was added
  createdAt: number;            // Timestamp
}

// Maximum images per visionboard
export const VISIONBOARD_MAX_IMAGES = 12;

// =============================================================================
// TEAM/PROJECT COLLABORATION TYPES
// =============================================================================

/**
 * Team member in a collaborative goal/project
 */
export interface TeamMember {
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoURL?: string;
  role: 'owner' | 'member' | 'va' | 'manager' | 'assistant';
  function?: string;                 // Optional function description (e.g., "Project Manager", "VA")
  accessLevel: 'all' | 'agenda-only'; // Access level: all pages or agenda only
  color: string;                     // For visual distinction (Figma-style)
  joinedAt: number;
  invitedBy: string;
}

/**
 * Project definition from AI sparring session
 */
export interface ProjectDefinition {
  projectType: string;               // What are we building?
  targetAudience: string;            // For whom?
  deliverables: string[];            // What will we deliver?
  scope: {
    included: string[];
    excluded: string[];
  };
  requiredRoles: string[];           // What skills/roles needed?
  estimatedDuration: string;         // Timeline
}

// =============================================================================
// KERN-INTENTIES & MIJLPAAL-CATEGORIEËN (Beslislaag)
// =============================================================================
//
// Deze types definiëren de onderliggende logica voor focus-bepaling.
// Ze worden NIET direct aan gebruikers getoond, maar worden gebruikt voor:
// - Detectie van onderliggende blokkades
// - Automatische categorisering van mijlpalen
// - Suggesties voor focus-gebieden
//
// BELANGRIJK:
// - Er is altijd maar 1 actieve kern-intentie tegelijk
// - Er is altijd maar 1 actieve mijlpaal-categorie tegelijk
// - De mapping tussen intentie en categorie is vast (zie INTENT_TO_CATEGORY_MAP)
// - Deze logica is canoniek en mag niet worden gewijzigd
// =============================================================================

/**
 * Kern-intenties: de onderliggende blokkades of uitdagingen
 * Dit zijn de 7 fundamentele redenen waarom doelen vastlopen
 */
export type CoreIntent =
  | 'onhelderheid'              // Doel/richting onduidelijk
  | 'onvoldoende_vaardigheid'   // Kennis/vaardigheden ontbreken
  | 'gebrek_aan_structuur'      // Systemen/processen ontbreken
  | 'gebrek_aan_externe_feedback' // Markt/feedback ontbreekt
  | 'opschaling_zonder_hefboom' // Groei niet schaalbaar
  | 'uitstel_vermijding'        // Actie wordt vermeden
  | 'overbelasting';            // Te veel tegelijk

/**
 * Mijlpaal-categorieën: de focus-gebieden voor mijlpalen
 * Elke categorie komt overeen met een kern-intentie
 */
export type MilestoneCategory =
  | 'richting_keuzes'      // Helderheid creëren
  | 'vaardigheden_kennis'  // Leren en ontwikkelen
  | 'systemen_structuur'   // Bouwen en organiseren
  | 'zichtbaarheid_markt'  // Extern valideren
  | 'opschaling_hefboom'   // Schalen en automatiseren
  | 'doorbraak_uitvoering'; // Actie nemen

/**
 * Vaste 1-op-1 mapping tussen kern-intenties en mijlpaal-categorieën
 * Deze mapping is canoniek en mag NOOIT worden gewijzigd
 */
export const INTENT_TO_CATEGORY_MAP: Record<CoreIntent, MilestoneCategory> = {
  onhelderheid: 'richting_keuzes',
  overbelasting: 'richting_keuzes', // Prioritering = helderheid
  onvoldoende_vaardigheid: 'vaardigheden_kennis',
  gebrek_aan_structuur: 'systemen_structuur',
  gebrek_aan_externe_feedback: 'zichtbaarheid_markt',
  opschaling_zonder_hefboom: 'opschaling_hefboom',
  uitstel_vermijding: 'doorbraak_uitvoering',
};

/**
 * Labels voor kern-intenties (voor interne logging, niet voor UI)
 */
export const CORE_INTENT_LABELS: Record<CoreIntent, string> = {
  onhelderheid: 'Onhelderheid',
  onvoldoende_vaardigheid: 'Onvoldoende vaardigheid',
  gebrek_aan_structuur: 'Gebrek aan structuur',
  gebrek_aan_externe_feedback: 'Gebrek aan externe feedback',
  opschaling_zonder_hefboom: 'Opschaling zonder hefboom',
  uitstel_vermijding: 'Uitstel / vermijding',
  overbelasting: 'Overbelasting',
};

/**
 * Labels voor mijlpaal-categorieën (voor UI)
 */
export const MILESTONE_CATEGORY_LABELS: Record<MilestoneCategory, string> = {
  richting_keuzes: 'Richting & Keuzes',
  vaardigheden_kennis: 'Vaardigheden & Kennis',
  systemen_structuur: 'Systemen & Structuur',
  zichtbaarheid_markt: 'Zichtbaarheid & Markt',
  opschaling_hefboom: 'Opschaling & Hefboom',
  doorbraak_uitvoering: 'Doorbraak & Uitvoering',
};

/**
 * Focus state: huidige actieve intentie en categorie voor een doel
 * Er kan maar 1 focus tegelijk actief zijn per doel
 */
export interface GoalFocus {
  id: string;
  goalId: string;           // Foreign key → Goal.id
  ownerId: string;          // Firebase Auth UID
  coreIntent: CoreIntent;   // Actieve kern-intentie
  category: MilestoneCategory; // Afgeleide categorie (via mapping)
  detectedAt: number;       // Timestamp van detectie
  confirmedByUser: boolean; // true als gebruiker dit heeft bevestigd
  autoDetected: boolean;    // true als automatisch gedetecteerd
  // Interne metadata (niet voor UI)
  detectionSignals?: string[]; // Welke signalen wezen op deze intentie
  confidenceScore?: number;    // 0-1, hoe zeker is de detectie
}

/**
 * Extended Milestone type met categorie (optioneel)
 * Backwards compatible: bestaande milestones zonder categorie blijven werken
 */
export interface CategorizedMilestone extends Milestone {
  category?: MilestoneCategory; // Categorie van deze mijlpaal
  focusId?: string;             // Link naar GoalFocus.id (als van focus afgeleid)
}
